<template>
    <div
        style="display: contents"
        v-if="hasFilters"
    >
        <action-button
            :icon="filtersAreShown ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
            v-if="totalFilters > 10"
            @click="filtersAreShown = !filtersAreShown"
        >
            {{ filtersAreShown ? $t('analytics.filters.hide') : $t('analytics.filters.show') }}
        </action-button>

        <action-button
            icon="cancel"
            v-if="filtersAreShown && showFromDate"
            @click="removeDateJob"
        >
            <template #before>
                <span class="font-semibold text-primary">{{ $t('analytics.jobs.date') }}:</span>
            </template>
            <span>
                <span v-if="getDateFromJobs !== ''" :class="getDateToJobs ? 'mr-4' : ''">
                    {{ $t('applications.filters.date_from') }} {{ getDateFromJobs }}
                </span>
                <span v-if="getDateToJobs !== ''">
                    {{ $t('applications.filters.date_to') }} {{ getDateToJobs }}
                </span>
            </span>
        </action-button>

        <action-button
            icon="cancel"
            v-if="filtersAreShown && (getDateFromApplications !== '' || getDateToApplications !== '')"
            @click="removeDateApplication"
        >
            <template #before>
                <span class="font-semibold text-primary">{{ $t('analytics.applications.date') }}:</span>
            </template>
            <span>
                <span v-if="getDateFromApplications !== ''" :class="getDateToApplications ? 'mr-4' : ''">
                    {{ $t('applications.filters.date_from') }} {{ getDateFromApplications }}
                </span>
                <span v-if="getDateToApplications !== ''">
                    {{ $t('applications.filters.date_to') }} {{ getDateToApplications }}
                </span>
            </span>
        </action-button>

        <action-button
            icon="cancel"
            :key="item.id"
            v-for="item in items"
            v-if="filtersAreShown"
            @click="removeCheckbox(item)"
        >
            <template #before v-if="item.prefix || item.group">
                <span class="font-semibold text-primary">{{ item.prefix ? $t(`analytics.filter_labels.${item.prefix}`) : (item.group ?? '') }}:</span>
            </template>
            {{ item.label }}
        </action-button>

        <action-button
            class="!bg-primary-20 hover:!bg-primary-60 !text-primary-80 hover:!text-white"
            icon="cancel"
            @click="removeAllFilters"
        >
            {{ $t('applications.filters.clear_filters') }}
        </action-button>
    </div>
</template>

<script>
import JobFunction from '../../models/JobFunction';
import Location from '../../models/Location';
import SvgIcon from '../ui/SvgIcon.vue';
import moment from 'moment';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import User from "../../models/User";
import JobReason from "../../models/JobReason";
import Interim from "../../models/Interim";
import Tag from "../../models/Tag";
import FilterGroup from "../../models/FilterGroup";
import ActionButton from "../ui/ActionButton.vue";

export default {
    name: 'ActiveFilters',
    components: {
        ActionButton,
        'svg-icon': SvgIcon,
    },
    props: {
        filterFields: Object,
        defaultFilters: Object,
    },
    data: function () {
        return {
            classes: 'mr-12 px-14 py-8 mb-8 last:mr-0 rounded-4 bg-white border border-primary-20 flex items-center z-10 hover:bg-primary-5 trans-ease',
            filtersAreShown: true,
        };
    },
    computed: {
        ...mapState(useMainStore, ['dateFormat', 'locale']),
        hasFilters() {
            return !!(this.getDateFromJobs
                || this.getDateToJobs
                || this.getDateFromApplications
                || this.getDateToApplications
                || this.items.length);
        },
        getDateFromJobs() {
            if (!this.filterFields.dateFromJobs) {
                return '';
            }

            return moment(this.filterFields.dateFromJobs).locale(this.locale).format(this.dateFormat);
        },
        getDateToJobs() {
            if (!this.filterFields.dateToJobs) {
                return '';
            }

            return moment(this.filterFields.dateToJobs).locale(this.locale).format(this.dateFormat);
        },
        getDateFromApplications() {
            if (!this.filterFields.dateFromApplications) {
                return '';
            }

            return moment(this.filterFields.dateFromApplications).locale(this.locale).format(this.dateFormat);
        },
        getDateToApplications() {
            if (!this.filterFields.dateToApplications) {
                return '';
            }

            return moment(this.filterFields.dateToApplications).locale(this.locale).format(this.dateFormat);
        },
        showFromDate() {
            return this.getDateFromJobs !== '' || this.getDateToJobs !== ''
        },
        items() {
            let items = [];

            if (this.filterFields.locationIds.length) {
                for (const id of this.filterFields.locationIds) {
                    const location = useRepo(Location).find(id);

                    if (location !== null) {
                        items.push({
                            prefix: 'location',
                            property: 'locationIds',
                            id: location.id,
                            label: location.alias && location.alias[this.locale]
                                ? location.alias[this.locale]
                                : `${location.street}, ${location.postal_code} ${location.city}, ${location.country}`,
                        });
                    }

                    if (id === 'other_locations') {
                        items.push({
                            prefix: 'location',
                            property: 'locationIds',
                            id: 'other_locations',
                            label: this.$t('analytics.filter_labels.other_locations')
                        });
                    }

                    if (id === 'remote') {
                        items.push({
                            prefix: 'location',
                            property: 'locationIds',
                            id: 'remote',
                            label: this.$t('analytics.filter_labels.remote')
                        });
                    }
                }
            }

            if (this.filterFields.regions.length) {
                for (const region of this.filterFields.regions) {
                    items.push({
                        prefix: 'regions',
                        property: 'regions',
                        id: region,
                        label: region
                    });
                }
            }

            if (this.filterFields.responsibleUserIds.length) {
                for (const id of this.filterFields.responsibleUserIds) {
                    const user = useRepo(User).find(id)

                    if (user !== null) {
                        items.push({
                            prefix: 'responsible_user',
                            property: 'responsibleUserIds',
                            id: user.id,
                            label: user.name
                        });
                    }
                }
            }

            if (this.filterFields.jobIds.length) {
                for (const id of this.filterFields.jobIds) {
                    const job = useRepo(JobFunction).find(id);

                    if (job !== null) {
                        items.push({
                            prefix: 'job',
                            property: 'jobIds',
                            id: job.id,
                            label: job.title[this.locale] + (job.city ? ' - ' + job.city : ''),
                        });
                    }
                }
            }

            if (this.filterFields.requestReasonIds.length) {
                for (const id of this.filterFields.requestReasonIds) {
                    const reason = useRepo(JobReason).find(id);

                    if (reason !== null) {
                        items.push({
                            prefix: 'request_reason',
                            property: 'requestReasonIds',
                            id: reason.id,
                            label: reason.title[this.locale],
                        });
                    }
                }
            }

            if (this.filterFields.rejectReasonIds.length) {
                for (const id of this.filterFields.rejectReasonIds) {
                    const reason = useRepo(JobReason).find(id);

                    if (reason !== null) {
                        items.push({
                            prefix: 'reject_reason',
                            property: 'rejectReasonIds',
                            id: reason.id,
                            label: reason.title[this.locale],
                        });
                    }
                }
            }

            if (this.filterFields.sourceIds.length) {
                for (const source of this.filterFields.sourceIds) {
                    if (source !== null) {
                        items.push({
                            prefix: 'source',
                            property: 'sourceIds',
                            id: source,
                            label: source,
                        });
                    }
                }
            }

            if (this.filterFields.officeIds.length) {
                for (const id of this.filterFields.officeIds) {
                    const office = useRepo(Interim).find(id);

                    if (office !== null) {
                        items.push({
                            prefix: 'office',
                            property: 'officeIds',
                            id: id,
                            label: office.name,
                        });
                    }
                }
            }

            if (this.filterFields.jobTagIds.length) {
                for (const id of this.filterFields.jobTagIds) {
                    const tag = useRepo(Tag).find(id);

                    if (tag) {
                        items.push({
                            prefix: 'job_tag',
                            property: 'jobTagIds',
                            id: id,
                            label: tag.title,
                        });
                    }
                }
            }

            if (this.filterFields.applicationTagIds.length) {
                for (const id of this.filterFields.applicationTagIds) {
                    const tag = useRepo(Tag).find(id);

                    if (tag) {
                        items.push({
                            prefix: 'application_tag',
                            property: 'applicationTagIds',
                            id: id,
                            label: tag.title,
                        });
                    }
                }
            }

            if (this.filterFields.filterGroups.length) {
                for (const id of this.filterFields.filterGroups) {
                    const filterGroup = useRepo(FilterGroup).find(id);

                    if (filterGroup) {
                        items.push({
                            prefix: 'filter_group',
                            property: 'filterGroups',
                            id: id,
                            label: filterGroup.title[this.locale],
                        });
                    }
                }
            }

            if (this.filterFields.filterGroupValues.length) {
                for (const id of this.filterFields.filterGroupValues) {
                    // find the filter group based on the filter group value
                    const filterGroups = useRepo(FilterGroup).all().filter(group => this.filterFields.filterGroups.includes(group.id));

                    if (filterGroups) {
                        // find the filter group value id in one of the filter groups, and set filterGroup to that group, and filterGroupValue to the value
                        let filterGroup = null;
                        let filterGroupValue = null;

                        for (const group of filterGroups) {
                            filterGroupValue = group.filters.find(filter => filter.id === id);

                            if (filterGroupValue) {
                                filterGroup = group;
                                break;
                            }
                        }

                        if (!filterGroupValue || !filterGroup) continue;
                        items.push({
                            group: filterGroup.title[this.locale],
                            property: 'filterGroupValues',
                            id: id,
                            label: filterGroupValue.title[this.locale],
                        })
                    }
                }
            }

            return items;
        },
        totalFilters() {
            let count = this.items.length;

            if (this.showFromDate) count++;
            if (this.getDateFromApplications !== '' || this.getDateToApplications !== '') count++;

            return count;
        }
    },
    methods: {
        removeCheckbox(checkbox) {
            if (typeof this.filterFields[checkbox.property] == 'undefined') {
                return;
            }

            let chk = []

            for (const item of this.filterFields[checkbox.property]) {
                if (item !== checkbox.id) {
                    chk.push(item);
                }
            }

            this.$emit('update', {
                [checkbox.property]: chk
            });
        },
        removeDateJob() {
            this.$emit('update', {
                dateFromJobs:null,
                dateToJobs: null
            });
        },
        removeDateApplication() {
            this.$emit('update', {
                dateFromApplications: null,
                dateToApplications: null
            });
        },
        removeStatuses() {
            this.filter.stages = [];
            this.filter.rejected = [];
            this.filter.hired = false;
            this.filter.new = false;
            this.filter.stale = false;
            this.filter.interview = false;

            this.$emit('update');
        },
        removeScore() {
            this.filter.score = false;

            this.$emit('update');
        },
        removeUnrated() {
            this.filter.unrated = -1;

            this.$emit('update');
        },
        removeAllFilters() {
            this.$emit('update', this.defaultFilters);
        },
        hideFilters() {
            this.$nextTick(() => {
                this.filtersAreShown = this.totalFilters <= 10;
            });
        }
    },
};
</script>
